import React from 'react';
import { Link } from 'react-router-dom';
import { FileText, ArrowLeft } from 'lucide-react';

const TermsOfUse = () => {
  return (
    <div className="max-w-4xl mx-auto">
      <div className="mb-6">
        <Link
          to="/events"
          className="inline-flex items-center text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100 transition-colors"
        >
          <ArrowLeft size={16} className="mr-2" />
          Back to Events
        </Link>
      </div>

      <div className="card">
        <div className="flex items-center space-x-3 mb-6">
          <FileText className="w-8 h-8 text-blue-600 dark:text-blue-400" />
          <h1 className="text-3xl font-bold text-gray-900 dark:text-gray-100">Terms of Use</h1>
        </div>

        <div className="prose prose-gray dark:prose-invert max-w-none">
          <p className="text-gray-600 dark:text-gray-400 mb-6">
            <strong>Last updated:</strong> {new Date().toLocaleDateString()}
          </p>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-900 dark:text-gray-100 mb-4">Acceptance of Terms</h2>
            <p className="text-gray-700 dark:text-gray-300 mb-4">
              By accessing and using the Blood on the Clocktower Event Signup Application ("the Application"), 
              you accept and agree to be bound by the terms and provision of this agreement.
            </p>
            <p className="text-gray-700 dark:text-gray-300">
              If you do not agree to abide by the above, please do not use this service.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-900 dark:text-gray-100 mb-4">Description of Service</h2>
            <p className="text-gray-700 dark:text-gray-300 mb-4">
              The Application is an event management platform designed specifically for Blood on the Clocktower games. 
              It provides the following services:
            </p>
            <ul className="list-disc pl-6 text-gray-700 dark:text-gray-300">
              <li>Event creation and management</li>
              <li>User registration and authentication via Discord</li>
              <li>Event signup and status tracking</li>
              <li>Event communication and notifications</li>
              <li>User profile management</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-900 dark:text-gray-100 mb-4">User Accounts and Registration</h2>
            <div className="space-y-4 text-gray-700 dark:text-gray-300">
              <p>
                To use certain features of the Application, you must register for an account using Discord OAuth. 
                You agree to provide accurate, current, and complete information during the registration process.
              </p>
              <p>
                You are responsible for safeguarding your Discord account credentials and for all activities that 
                occur under your account. You agree to notify us immediately of any unauthorized use of your account.
              </p>
              <p>
                You must be at least 13 years old to use the Application. If you are under 18, you must have 
                parental or guardian consent to use the service.
              </p>
            </div>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-900 dark:text-gray-100 mb-4">Acceptable Use</h2>
            <p className="text-gray-700 dark:text-gray-300 mb-4">You agree not to use the Application to:</p>
            <ul className="list-disc pl-6 text-gray-700 dark:text-gray-300">
              <li>Violate any applicable laws or regulations</li>
              <li>Infringe upon the rights of others</li>
              <li>Harass, abuse, or harm other users</li>
              <li>Post inappropriate, offensive, or harmful content</li>
              <li>Attempt to gain unauthorized access to the Application or other users' accounts</li>
              <li>Use the Application for commercial purposes without permission</li>
              <li>Interfere with or disrupt the Application's functionality</li>
              <li>Create multiple accounts for malicious purposes</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-900 dark:text-gray-100 mb-4">Event Management</h2>
            <div className="space-y-4 text-gray-700 dark:text-gray-300">
              <p>
                Event creators and administrators have additional responsibilities and permissions within the Application. 
                By creating or managing events, you agree to:
              </p>
              <ul className="list-disc pl-6">
                <li>Provide accurate and complete event information</li>
                <li>Respect the privacy and preferences of event participants</li>
                <li>Use the Application's features responsibly and ethically</li>
                <li>Comply with all applicable laws and regulations regarding event management</li>
              </ul>
              <p>
                The Application reserves the right to remove events or restrict access to users who violate these terms.
              </p>
            </div>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-900 dark:text-gray-100 mb-4">Intellectual Property</h2>
            <div className="space-y-4 text-gray-700 dark:text-gray-300">
              <p>
                The Application and its original content, features, and functionality are owned by the developers 
                and are protected by international copyright, trademark, patent, trade secret, and other intellectual 
                property laws.
              </p>
              <p>
                You retain ownership of any content you submit to the Application, but you grant us a license to use, 
                display, and distribute such content in connection with the Application's services.
              </p>
            </div>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-900 dark:text-gray-100 mb-4">Privacy and Data Protection</h2>
            <p className="text-gray-700 dark:text-gray-300 mb-4">
              Your privacy is important to us. Please review our Privacy Policy, which also governs your use of the Application, 
              to understand our practices regarding the collection and use of your personal information.
            </p>
            <p className="text-gray-700 dark:text-gray-300">
              By using the Application, you consent to the collection and use of information as outlined in our Privacy Policy.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-900 dark:text-gray-100 mb-4">Disclaimers</h2>
            <div className="space-y-4 text-gray-700 dark:text-gray-300">
              <p>
                The Application is provided "as is" and "as available" without any warranties of any kind, either express or implied.
              </p>
              <p>
                We do not guarantee that the Application will be uninterrupted, secure, or error-free, or that any defects 
                will be corrected.
              </p>
              <p>
                We are not responsible for the content, accuracy, or reliability of any information provided by users 
                or third parties through the Application.
              </p>
            </div>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-900 dark:text-gray-100 mb-4">Limitation of Liability</h2>
            <p className="text-gray-700 dark:text-gray-300">
              In no event shall the Application, its developers, or its affiliates be liable for any indirect, incidental, 
              special, consequential, or punitive damages, including without limitation, loss of profits, data, use, goodwill, 
              or other intangible losses, resulting from your use of the Application.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-900 dark:text-gray-100 mb-4">Termination</h2>
            <div className="space-y-4 text-gray-700 dark:text-gray-300">
              <p>
                We may terminate or suspend your account and access to the Application immediately, without prior notice 
                or liability, for any reason whatsoever, including without limitation if you breach the Terms.
              </p>
              <p>
                Upon termination, your right to use the Application will cease immediately. If you wish to terminate your account, 
                you may simply discontinue using the Application or contact us to request account deletion.
              </p>
            </div>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-900 dark:text-gray-100 mb-4">Governing Law</h2>
            <p className="text-gray-700 dark:text-gray-300">
              These Terms shall be interpreted and governed by the laws of the jurisdiction in which the Application 
              is operated, without regard to its conflict of law provisions.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-900 dark:text-gray-100 mb-4">Changes to Terms</h2>
            <p className="text-gray-700 dark:text-gray-300">
              We reserve the right, at our sole discretion, to modify or replace these Terms at any time. 
              If a revision is material, we will try to provide at least 30 days notice prior to any new terms 
              taking effect. What constitutes a material change will be determined at our sole discretion.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-900 dark:text-gray-100 mb-4">Contact Information</h2>
            <p className="text-gray-700 dark:text-gray-300">
              If you have any questions about these Terms of Use, please contact us through the Discord server 
              or through the application's support channels.
            </p>
          </section>
        </div>
      </div>
    </div>
  );
};

export default TermsOfUse; 